import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CalendarCheck, CalendarDays, ChevronRight, GraduationCap, Instagram, MapPin, MessageCircle, Video } from 'lucide-react'
import { analyticsService } from '@meiuca/analytics'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { MarketplaceFooter } from '@/components/MarketplaceFooter'
import { trainingCenterService } from '@/services/trainingCenterService'
import {
  getLevelLabel,
  getModalityLabel,
  getLevelTagClass,
  type TrainingCenterDetail
} from '@/types/trainingCenter'

export function TrainingCenterDetailPage() {
  const { centerId } = useParams<{ centerId: string }>()
  const navigate = useNavigate()

  const [center, setCenter] = useState<TrainingCenterDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [showMoreInfo, setShowMoreInfo] = useState(false)

  useEffect(() => {
    analyticsService.trackScreenView('ct_detail')
  }, [])

  const loadCenter = useCallback(async () => {
    if (!centerId) return

    try {
      setLoading(true)
      setNotFound(false)
      setCenter(await trainingCenterService.getById(centerId))
    } catch (err) {
      console.error('[TrainingCenterDetailPage] Error loading center:', err)
      // Um centro que saiu da vitrine responde 404 igual a um id inexistente —
      // do lado de cá as duas situações são a mesma tela.
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }, [centerId])

  useEffect(() => {
    void loadCenter()
  }, [loadCenter])

  const hasSchedule = (center?.weeklySchedule.length ?? 0) > 0

  const handleWhatsappClick = () => {
    if (!center?.whatsappNumber) return
    analyticsService.track('ct_whatsapp_clicked', { ct_id: center.id })
    window.open(`https://wa.me/${center.whatsappNumber.replace(/\D/g, '')}`, '_blank')
  }

  const handleInstagramClick = () => {
    if (!center?.instagramHandle) return
    analyticsService.track('ct_instagram_clicked', { ct_id: center.id })
    window.open(`https://instagram.com/${center.instagramHandle}`, '_blank')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-secondary">
        <MarketplaceHeader onLocationClick={() => {}} />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  if (notFound || !center) {
    return (
      <div className="min-h-screen bg-background-secondary flex flex-col">
        <MarketplaceHeader onLocationClick={() => {}} />
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center py-32">
          <GraduationCap className="w-12 h-12 text-text-tertiary mb-4" />
          <p className="text-text-primary font-medium">Centro não disponível</p>
          <p className="text-text-secondary text-sm mt-1 max-w-md">
            Este centro de treinamento não está mais na vitrine do app.
          </p>
          <button
            onClick={() => navigate('/cts/search')}
            className="mt-6 px-4 py-2 rounded-lg bg-brand-primary text-white font-medium"
          >
            Ver outros centros
          </button>
        </div>
        <MarketplaceFooter />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-secondary flex flex-col">
      <MarketplaceHeader onLocationClick={() => {}} />

      <div
        className={`flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 ${
          center.whatsappNumber ? 'pb-28' : ''
        }`}
      >
        {/* Header unificado: capa, logo, nome e apresentação num card contínuo.
            Separados, o logo flutuava meio para fora entre a capa e o card de
            texto e parecia cortado; junto, cada peça tem onde se apoiar.

            A capa é 3:1 fixo, a mesma proporção que o recorte do dashboard
            produz — full-bleed no desktop virava uma faixa de 6,7:1 e o
            object-cover decapitava o enquadramento que o dono acabara de
            fazer. */}
        <section className="overflow-hidden rounded-xl border border-border-light bg-background-white">
          <div className="relative">
            <div className="aspect-[3/1] w-full bg-gradient-to-br from-brand-primary/30 to-brand-primary/10">
              {center.coverImageURL && (
                <img
                  src={center.coverImageURL}
                  alt={center.name}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <button
              onClick={() => navigate(-1)}
              className="absolute top-3 left-3 rounded-full bg-background-white/90 p-2 shadow-md backdrop-blur-sm"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-5 h-5 text-text-primary" />
            </button>
          </div>

          <div className="space-y-4 p-5">
            <div className="flex items-start gap-4">
              {center.logoURL && (
                // `contain` com respiro: logo tem margem própria, e recorte
                // apertado corta justamente a marca.
                // `relative z-10`: o wrapper da capa é posicionado (pelo botão
                // voltar) e pintaria por cima do logo em fluxo normal — o logo
                // precisa entrar no empilhamento para vencer. Metade dentro,
                // metade fora (-mt-10 em 80px) é a sobreposição clássica.
                <img
                  src={center.logoURL}
                  alt={`Logo de ${center.name}`}
                  className="relative z-10 -mt-10 h-20 w-20 flex-shrink-0 rounded-xl border border-border-light bg-white object-contain p-2 shadow-md"
                />
              )}

              <div className="min-w-0 flex-1 space-y-1.5">
                <h1 className="break-words text-2xl font-bold text-text-primary">{center.name}</h1>

                <div className="flex flex-wrap items-center gap-1.5">
                  {center.modalities.map((modality) => (
                    <span
                      key={modality}
                      className="inline-flex items-center rounded bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary"
                    >
                      {getModalityLabel(modality)}
                    </span>
                  ))}
                  {center.offersRemoteConsulting && (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      <Video className="w-3 h-3" />
                      Atende online
                    </span>
                  )}
                  {center.instagramHandle && (
                    <button
                      onClick={handleInstagramClick}
                      className="inline-flex items-center gap-1 text-xs font-medium text-text-secondary hover:text-text-primary"
                    >
                      <Instagram className="h-3.5 w-3.5" />@{center.instagramHandle}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {center.publicDescription && (
              <p
                className={`text-text-secondary whitespace-pre-line ${
                  showMoreInfo ? '' : 'line-clamp-3'
                }`}
              >
                {center.publicDescription}
              </p>
            )}

            {center.levels.length > 0 && (
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm text-text-tertiary">Níveis</span>
                <div className="flex flex-wrap gap-1">
                  {center.levels.map((level) => (
                    <span
                      key={level}
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${getLevelTagClass(level)}`}
                    >
                      {getLevelLabel(level)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {center.locations.length > 0 && (
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm text-text-tertiary">
                  {center.locations.length === 1 ? 'Local' : 'Locais'}
                </span>
                <p className="text-sm text-text-primary">
                  {center.locations.map((location) => location.name).join(' · ')}
                </p>
              </div>
            )}

            {showMoreInfo && center.locations.length > 0 && (
              <div className="space-y-3 border-t border-border-light pt-3">
                {center.locations.map((location) => (
                  <a
                    key={location.id}
                    href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 transition-opacity hover:opacity-80"
                  >
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-text-tertiary" />
                    <span className="text-sm">
                      <span className="block font-medium text-text-primary">{location.name}</span>
                      <span className="block text-text-secondary">{location.address}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}

            {(center.publicDescription || center.locations.length > 0) && (
              <button
                type="button"
                onClick={() => setShowMoreInfo((current) => !current)}
                className="text-sm font-medium text-brand-primary hover:underline"
              >
                {showMoreInfo ? 'Ver menos' : 'Saiba mais'}
              </button>
            )}
          </div>
        </section>

        {/* Bloco de ação: a vitrine é curta e direcional — marcar aula (agenda
            datada, com o catálogo de pacotes dentro do checkout quando faltar
            crédito) e ver turmas (a grade semanal, informativa, em tela
            própria). */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* "Marcar aula" só para quem já é cliente (o detalhe da vitrine
              traz `isCustomer` quando a request é autenticada): a agenda e a
              compra de pacote exigem o vínculo no backend — para os demais,
              o caminho é o contato. */}
          {center.isCustomer ? (
            <button
              type="button"
              onClick={() => {
                analyticsService.track('ct_book_class_clicked', { ct_id: center.id })
                navigate(`/reposicoes/${center.id}`)
              }}
              className="flex items-center gap-3 rounded-xl bg-brand-primary p-4 text-left text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <CalendarCheck className="h-6 w-6 flex-shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">Marcar aula</span>
                <span className="block text-sm text-white/80">Veja os horários e garanta sua vaga</span>
              </span>
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/80" />
            </button>
          ) : center.whatsappNumber ? (
            <button
              type="button"
              onClick={handleWhatsappClick}
              className="flex items-center gap-3 rounded-xl bg-brand-primary p-4 text-left text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <MessageCircle className="h-6 w-6 flex-shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold">Entrar em contato</span>
                <span className="block text-sm text-white/80">
                  Fale com o centro para começar a treinar
                </span>
              </span>
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-white/80" />
            </button>
          ) : null}

          {hasSchedule && (
            <button
              type="button"
              onClick={() => {
                analyticsService.track('ct_view_classes_clicked', { ct_id: center.id })
                navigate(`/ct/${center.id}/turmas`)
              }}
              className="flex items-center gap-3 rounded-xl border border-border-light bg-background-white p-4 text-left shadow-sm transition-colors hover:bg-background-tertiary"
            >
              <CalendarDays className="h-6 w-6 flex-shrink-0 text-brand-primary" />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-text-primary">Ver turmas</span>
                <span className="block text-sm text-text-secondary">Grade semanal, níveis e locais</span>
              </span>
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-text-tertiary" />
            </button>
          )}
        </section>
      </div>

      {/* Barra fixa com o CTA: a página é longa (grade semanal inteira) e a
          decisão de falar com o centro acontece a qualquer altura da rolagem —
          fazer subir de volta para achar o botão é atrito puro. */}
      {center.whatsappNumber && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border-light bg-background-white/95 backdrop-blur-sm px-4 py-3 shadow-lg">
          <div className="mx-auto max-w-4xl">
            <button
              onClick={handleWhatsappClick}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-3 font-medium text-white transition-opacity hover:opacity-90"
            >
              <MessageCircle className="w-5 h-5" />
              Falar no WhatsApp
            </button>
          </div>
        </div>
      )}

      <MarketplaceFooter />
    </div>
  )
}
